import React, { useCallback, useEffect, useState } from 'react';
import {
  ManagedUser,
  UserRole,
  createUser,
  listUsers,
  updateUser,
} from '../auth/api';
import { useAuth } from '../auth/AuthContext';
import { SessionBadge } from './SessionBadge';
import { navigate } from './EventsList';
import './EventsList.css';
import './AdminUsers.css';

function formatDateTime(iso: string | null): string {
  if (!iso) return 'never';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function errorText(err: unknown, fallback: string): string {
  return err instanceof Error ? err.message : fallback;
}

export const AdminUsers: React.FC = () => {
  const { user: currentUser } = useAuth();

  const [users, setUsers] = useState<ManagedUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [username, setUsername] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState('');
  const [role, setRole] = useState<UserRole>('user');
  const [creating, setCreating] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  /** Id of the row currently awaiting a request, so its buttons can be disabled. */
  const [busyId, setBusyId] = useState<string | null>(null);
  const [resetFor, setResetFor] = useState<string | null>(null);
  const [resetValue, setResetValue] = useState('');

  const refresh = useCallback(async (): Promise<void> => {
    try {
      setUsers(await listUsers());
      setError(null);
    } catch (err) {
      setError(errorText(err, 'Failed to load users'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const handleCreate = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (creating) return;

    setCreating(true);
    setFormError(null);
    setNotice(null);
    try {
      const created = await createUser({
        username,
        password,
        displayName,
        role,
      });
      setUsername('');
      setDisplayName('');
      setPassword('');
      setRole('user');
      setNotice(`Created "${created.username}".`);
      await refresh();
    } catch (err) {
      setFormError(errorText(err, 'Could not create the account'));
    } finally {
      setCreating(false);
    }
  };

  const applyChange = async (
    id: string,
    change: Parameters<typeof updateUser>[1],
    successMessage: string,
  ): Promise<void> => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      await updateUser(id, change);
      setNotice(successMessage);
      await refresh();
    } catch (err) {
      setError(errorText(err, 'Could not update the account'));
    } finally {
      setBusyId(null);
    }
  };

  const handleResetPassword = async (id: string): Promise<void> => {
    await applyChange(id, { password: resetValue }, 'Password updated.');
    setResetFor(null);
    setResetValue('');
  };

  return (
    <div className="App">
      <div className="app-header">
        <h1>Karting Live Timing</h1>
        <SessionBadge />
      </div>
      <div className="app-main">
        <div className="main-content">
          <div className="events-page">
            <div className="events-toolbar">
              <h2 className="events-title">Accounts</h2>
              <button
                className="events-link-button"
                onClick={() => navigate('/')}
              >
                ← Back to live
              </button>
            </div>

            <form className="admin-create" onSubmit={handleCreate}>
              <h3 className="events-section-title">Add an account</h3>
              <div className="admin-create-fields">
                <label className="admin-field">
                  <span>Username</span>
                  <input
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="lowercase, no spaces"
                    autoCapitalize="none"
                    autoCorrect="off"
                    spellCheck={false}
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Display name</span>
                  <input
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="optional"
                  />
                </label>
                <label className="admin-field">
                  <span>Password</span>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="at least 8 characters"
                    autoComplete="new-password"
                    required
                  />
                </label>
                <label className="admin-field">
                  <span>Role</span>
                  <select
                    value={role}
                    onChange={(e) => setRole(e.target.value as UserRole)}
                  >
                    <option value="user">Viewer</option>
                    <option value="admin">Admin</option>
                  </select>
                </label>
                <button
                  className="admin-create-submit"
                  type="submit"
                  disabled={creating || !username.trim() || !password}
                >
                  {creating ? 'Creating…' : 'Create'}
                </button>
              </div>
              <p className="admin-hint">
                Admins can start and stop races and manage accounts. Viewers get
                the timing screens only.
              </p>
              {formError && <div className="events-error">{formError}</div>}
            </form>

            {notice && <div className="admin-notice">{notice}</div>}
            {error && <div className="events-error">Error: {error}</div>}

            {loading ? (
              <div className="events-loading">Loading accounts…</div>
            ) : (
              <div className="events-table-wrapper">
                <table className="events-table">
                  <thead>
                    <tr>
                      <th>Username</th>
                      <th>Display name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => {
                      const isSelf = u.id === currentUser?.id;
                      const busy = busyId === u.id;
                      return (
                        <React.Fragment key={u.id}>
                          <tr>
                            <td>
                              {u.username}
                              {isSelf && <span className="admin-you">you</span>}
                            </td>
                            <td>{u.displayName || '—'}</td>
                            <td>
                              <span className={`role-badge role-${u.role}`}>
                                {u.role === 'admin' ? 'ADMIN' : 'VIEWER'}
                              </span>
                            </td>
                            <td>
                              <span
                                className={`status-badge ${
                                  u.isActive ? 'status-live' : 'status-finished'
                                }`}
                              >
                                {u.isActive ? 'ACTIVE' : 'DISABLED'}
                              </span>
                            </td>
                            <td>{formatDateTime(u.lastLoginAt)}</td>
                            <td className="admin-actions">
                              <button
                                className="events-link-button"
                                disabled={busy}
                                onClick={() =>
                                  void applyChange(
                                    u.id,
                                    {
                                      role:
                                        u.role === 'admin' ? 'user' : 'admin',
                                    },
                                    `"${u.username}" is now ${
                                      u.role === 'admin' ? 'a viewer' : 'an admin'
                                    }.`,
                                  )
                                }
                              >
                                {u.role === 'admin'
                                  ? 'Make viewer'
                                  : 'Make admin'}
                              </button>
                              <button
                                className="events-link-button"
                                disabled={busy || isSelf}
                                title={
                                  isSelf
                                    ? 'You cannot disable your own account'
                                    : undefined
                                }
                                onClick={() =>
                                  void applyChange(
                                    u.id,
                                    { isActive: !u.isActive },
                                    `"${u.username}" ${
                                      u.isActive ? 'disabled' : 'enabled'
                                    }.`,
                                  )
                                }
                              >
                                {u.isActive ? 'Disable' : 'Enable'}
                              </button>
                              <button
                                className="events-link-button"
                                disabled={busy}
                                onClick={() => {
                                  // Reopening on another row must not carry the
                                  // previous row's typed value over.
                                  setResetValue('');
                                  setResetFor(resetFor === u.id ? null : u.id);
                                }}
                              >
                                Reset password
                              </button>
                            </td>
                          </tr>
                          {resetFor === u.id && (
                            <tr className="admin-reset-row">
                              <td colSpan={6}>
                                <div className="admin-reset">
                                  <input
                                    type="password"
                                    value={resetValue}
                                    onChange={(e) =>
                                      setResetValue(e.target.value)
                                    }
                                    placeholder={`New password for ${u.username}`}
                                    autoComplete="new-password"
                                  />
                                  <button
                                    className="events-link-button"
                                    disabled={busy || resetValue.length < 8}
                                    onClick={() =>
                                      void handleResetPassword(u.id)
                                    }
                                  >
                                    Save
                                  </button>
                                  <button
                                    className="events-link-button"
                                    onClick={() => {
                                      setResetFor(null);
                                      setResetValue('');
                                    }}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
